import { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    TextField,
    Stack,
    Tabs,
    Tab,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Alert,
    Divider,
} from '@mui/material';
import { Close as CloseIcon, ReceiptLong as ReceiptIcon, HelpOutline as HelpIcon } from '@mui/icons-material';
import { CONSULTAR_FATURAS_DEFAULTS } from './FlowBuilderConsultarFaturasModal';

export const CONSULTAR_FATURAS_V2_DEFAULTS = {
    ...CONSULTAR_FATURAS_DEFAULTS,
    chooseContractMessage: '📋 Localizei {{quantidade_contratos}} Contratos vinculados ao seu cadastro.\n\n👇 Selecione qual deseja consultar:',
    invalidContractChoiceMessage: '❌ Opcao inválida. Informe o número da lista ou o Nº do contrato.',
};

const THEME = '#1565c0';
const THEME_HOVER = '#0d47a1';

interface FlowBuilderConsultarFaturasV2ModalProps {
    open: boolean;
    onClose: () => void;
    onSave: (data: Record<string, any>) => void;
    data?: Record<string, any>;
}

const FlowBuilderConsultarFaturasV2Modal = ({
    open,
    onClose,
    onSave,
    data,
}: FlowBuilderConsultarFaturasV2ModalProps) => {
    const [activeTab, setActiveTab] = useState(0);
    const [helpOpen, setHelpOpen] = useState(false);
    const [form, setForm] = useState<Record<string, any>>({ ...CONSULTAR_FATURAS_V2_DEFAULTS, label: 'Consultar Faturas V2' });

    useEffect(() => {
        if (open) {
            setForm({ ...CONSULTAR_FATURAS_V2_DEFAULTS, label: 'Consultar Faturas V2', ...data });
            setActiveTab(0);
        }
    }, [open, data]);

    useEffect(() => {
        if (form.sendMode === 'link' && activeTab > 2) {
            setActiveTab(0);
        }
    }, [form.sendMode, activeTab]);

    const update = (patch: Record<string, any>) => {
        setForm((prev) => ({ ...prev, ...patch }));
    };

    const handleSave = () => {
        onSave(form);
        onClose();
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ReceiptIcon sx={{ color: THEME }} />
                        <Typography variant="h6" component="span">Consultar Faturas V2</Typography>
                        <IconButton size="small" onClick={() => setHelpOpen(true)} title="Como funciona este no" sx={{ color: THEME }}>
                            <HelpIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    <Stack spacing={2.5}>
                        <Alert severity="info" sx={{ mb: 0 }}>
                            V2: seleciona contrato quando houver faturas de contratos diferentes. Saidas: Sem cliente · OK · Sem faturas · Sem solucao
                        </Alert>

                        <TextField
                            label="Nome do no"
                            fullWidth
                            size="small"
                            value={form.label || ''}
                            onChange={(e) => update({ label: e.target.value })}
                        />

                        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
                            <Tab label="Geral" />
                            <Tab label="Identificacao" />
                            <Tab label="Contratos e faturas" />
                            {form.sendMode !== 'link' && <Tab label="Modo dados" />}
                        </Tabs>

                        {activeTab === 0 && (
                            <Stack spacing={2}>
                                <TextField
                                    label="Quantidade de faturas na lista"
                                    type="number"
                                    fullWidth
                                    size="small"
                                    inputProps={{ min: 1, max: 20 }}
                                    value={form.quantidade ?? 3}
                                    onChange={(e) => update({ quantidade: parseInt(e.target.value, 10) || 3 })}
                                />
                                <FormControl fullWidth size="small">
                                    <InputLabel>Filtro de status</InputLabel>
                                    <Select
                                        value={form.statusFilter || 'abertas'}
                                        label="Filtro de status"
                                        onChange={(e) => update({ statusFilter: e.target.value })}
                                    >
                                        <MenuItem value="abertas">Abertas (aberto + vencido)</MenuItem>
                                        <MenuItem value="aberto">Somente aberto</MenuItem>
                                        <MenuItem value="vencido">Somente vencido</MenuItem>
                                        <MenuItem value="todas">Todas (exceto canceladas)</MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Modo de envio</InputLabel>
                                    <Select
                                        value={form.sendMode || 'dados'}
                                        label="Modo de envio"
                                        onChange={(e) => update({ sendMode: e.target.value })}
                                    >
                                        <MenuItem value="dados">Dados da fatura (linha digitavel + PIX)</MenuItem>
                                        <MenuItem value="link">Link (template enviar_fatura)</MenuItem>
                                    </Select>
                                </FormControl>
                                {form.sendMode === 'link' && (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={Boolean(form.enviarPdf)}
                                                onChange={(e) => update({ enviarPdf: e.target.checked })}
                                            />
                                        }
                                        label="Anexar PDF da fatura (se template permitir)"
                                    />
                                )}
                                <TextField
                                    label="Max tentativas CPF"
                                    type="number"
                                    fullWidth
                                    size="small"
                                    inputProps={{ min: 1, max: 10 }}
                                    value={form.maxCpfAttempts ?? 3}
                                    onChange={(e) => update({ maxCpfAttempts: parseInt(e.target.value, 10) || 3 })}
                                />
                                <Divider />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={Boolean(form.limitarDiasVencidas)}
                                            onChange={(e) => update({ limitarDiasVencidas: e.target.checked })}
                                        />
                                    }
                                    label="Nao enviar faturas com mais de X dias vencidas"
                                />
                                {form.limitarDiasVencidas && (
                                    <TextField
                                        label="Maximo de dias vencidas"
                                        type="number"
                                        fullWidth
                                        size="small"
                                        inputProps={{ min: 1, max: 3650 }}
                                        value={form.maxDiasVencidas ?? 90}
                                        onChange={(e) => update({ maxDiasVencidas: parseInt(e.target.value, 10) || 90 })}
                                    />
                                )}
                            </Stack>
                        )}

                        {activeTab === 1 && (
                            <Stack spacing={2}>
                                <TextField label="Pedir CPF/CNPJ" fullWidth multiline rows={2} size="small" value={form.askCpfMessage || ''} onChange={(e) => update({ askCpfMessage: e.target.value })} />
                                <TextField label="CPF nao encontrado" fullWidth multiline rows={2} size="small" value={form.cpfNotFoundMessage || ''} onChange={(e) => update({ cpfNotFoundMessage: e.target.value })} />
                                <TextField label="Confirmar identidade" fullWidth multiline rows={3} size="small" value={form.confirmIdentityMessage || ''} onChange={(e) => update({ confirmIdentityMessage: e.target.value })} helperText="Use {{nome_cliente}}" />
                                <TextField label="Identidade negada" fullWidth multiline rows={2} size="small" value={form.identityDeniedMessage || ''} onChange={(e) => update({ identityDeniedMessage: e.target.value })} />
                                <TextField label="Consentimento de vinculo" fullWidth multiline rows={3} size="small" value={form.askLinkConsentMessage || ''} onChange={(e) => update({ askLinkConsentMessage: e.target.value })} />
                            </Stack>
                        )}

                        {activeTab === 2 && (
                            <Stack spacing={2}>
                                <Typography variant="caption" color="text.secondary" fontWeight="bold">Selecao de contrato (multiplos contratos)</Typography>
                                <TextField
                                    label="Introducao escolha de contrato"
                                    fullWidth
                                    multiline
                                    rows={2}
                                    size="small"
                                    value={form.chooseContractMessage || ''}
                                    onChange={(e) => update({ chooseContractMessage: e.target.value })}
                                    helperText="Use {{quantidade_contratos}}. Lista: 1️⃣ - #ID - endereco"
                                />
                                <TextField label="Contrato invalido" fullWidth multiline rows={2} size="small" value={form.invalidContractChoiceMessage || ''} onChange={(e) => update({ invalidContractChoiceMessage: e.target.value })} />
                                <Divider />
                                <Typography variant="caption" color="text.secondary" fontWeight="bold">Listagem de faturas</Typography>
                                <TextField label="Introducao da lista" fullWidth multiline rows={2} size="small" value={form.listIntroMessage || ''} onChange={(e) => update({ listIntroMessage: e.target.value })} />
                                <TextField label="Sem faturas" fullWidth multiline rows={2} size="small" value={form.noInvoicesMessage || ''} onChange={(e) => update({ noInvoicesMessage: e.target.value })} />
                                <TextField label="Escolha invalida (fatura)" fullWidth multiline rows={2} size="small" value={form.invalidChoiceMessage || ''} onChange={(e) => update({ invalidChoiceMessage: e.target.value })} />
                                {form.limitarDiasVencidas && (
                                    <TextField label="Faturas expiradas (limite dias)" fullWidth multiline rows={2} size="small" value={form.faturasExpiradasMessage || ''} onChange={(e) => update({ faturasExpiradasMessage: e.target.value })} />
                                )}
                                {form.sendMode !== 'link' && (
                                    <TextField label="Sem dados de pagamento (sem solucao)" fullWidth multiline rows={2} size="small" value={form.semDadosPagamentoMessage || ''} onChange={(e) => update({ semDadosPagamentoMessage: e.target.value })} helperText="Quando a fatura nao tiver linha digitavel nem PIX." />
                                )}
                            </Stack>
                        )}

                        {activeTab === 3 && form.sendMode !== 'link' && (
                            <Stack spacing={2}>
                                <TextField label="Mensagem 1 (opcional)" fullWidth multiline rows={2} size="small" value={form.mensagem1 || ''} onChange={(e) => update({ mensagem1: e.target.value })} />
                                <TextField label="Mensagem 2 (opcional)" fullWidth multiline rows={2} size="small" value={form.mensagem2 || ''} onChange={(e) => update({ mensagem2: e.target.value })} />
                                <TextField label="Mensagem 3 (opcional)" fullWidth multiline rows={2} size="small" value={form.mensagem3 || ''} onChange={(e) => update({ mensagem3: e.target.value })} />
                                <TextField label="Mensagem 4 (encerramento)" fullWidth multiline rows={2} size="small" value={form.mensagem4 || ''} onChange={(e) => update({ mensagem4: e.target.value })} />
                            </Stack>
                        )}
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={onClose} color="inherit">Cancelar</Button>
                    <Button onClick={handleSave} variant="contained" sx={{ bgcolor: THEME, '&:hover': { bgcolor: THEME_HOVER } }}>
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="sm" fullWidth scroll="paper">
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6">Como funciona o no Consultar Faturas V2</Typography>
                    <IconButton size="small" onClick={() => setHelpOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2.5}>
                        <Typography variant="body2" color="text.secondary">
                            Versao aprimorada do Consultar Faturas. Inclui tudo do V1 e adiciona selecao de contrato quando o cliente possui faturas de contratos diferentes.
                        </Typography>

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>1. Identificacao do cliente</Typography>
                            <Typography variant="body2" component="div" color="text.secondary">
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                    <li>Se o contato ja tem cliente vinculado, pula direto para a consulta.</li>
                                    <li>Se nao, pede CPF/CNPJ, confirma identidade e pergunta consentimento de vinculo permanente.</li>
                                    <li>Mesmo fluxo de identificacao do no Consultar Faturas (V1).</li>
                                </ul>
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>2. Selecao de contrato</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Apos identificar o cliente, o sistema agrupa as faturas por contrato (campo <em>endereco_inst</em>).
                            </Typography>
                            <Typography variant="body2" component="div" color="text.secondary" sx={{ mt: 1 }}>
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                    <li>Se <strong>todas as faturas</strong> pertencem ao <strong>mesmo contrato</strong>, segue direto para listar ou enviar faturas — sem perguntar.</li>
                                    <li>Se houver faturas de <strong>2 ou mais contratos</strong>, pergunta qual consultar:</li>
                                </ul>
                            </Typography>
                            <Typography component="div" variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', px: 1, py: 0.5, borderRadius: 1, mt: 1 }}>
                                Localizei 2 Contratos, qual deseja consultar:<br />
                                1️⃣ - #501 - Rua Alpha, 100<br />
                                2️⃣ - #502 - Av Beta, 200
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                O cliente responde pelo emoji, numero da lista ou ID do contrato. Apos a escolha, apenas as faturas daquele contrato sao listadas ou enviadas.
                                <br /><br />
                                <strong>Importante:</strong> todos os contratos com faturas aparecem na lista, mesmo que um deles tenha apenas faturas vencidas ha mais de X dias. O limite de dias e aplicado <em>depois</em> da escolha do contrato.
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>3. Listagem e escolha de fatura</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Dentro do contrato escolhido, lista ate a quantidade configurada:
                            </Typography>
                            <Typography component="div" variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', px: 1, py: 0.5, borderRadius: 1, mt: 0.5 }}>
                                1️⃣ - Referencia: 2026-06 - Vencimento: 15/06/2026 - Valor: R$ 99,90
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Se existir <strong>apenas 1 fatura</strong> no contrato, ela e enviada automaticamente.
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>4. Modos de envio</Typography>
                            <Typography variant="body2" component="div" color="text.secondary">
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                    <li><strong>Dados:</strong> envia valor, vencimento, linha digitavel e PIX.</li>
                                    <li><strong>Link:</strong> usa o template WhatsApp <em>enviar_fatura</em> (opcionalmente com PDF).</li>
                                </ul>
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>5. Validacoes antes do envio</Typography>
                            <Typography variant="body2" component="div" color="text.secondary">
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                    <li><strong>Limite de dias vencidas:</strong> aplicado ao contrato escolhido. Se todas as faturas daquele contrato ultrapassarem o limite, saida <strong>Sem solucao</strong>.</li>
                                    <li><strong>Dados de pagamento (modo Dados):</strong> se a fatura nao tiver linha digitavel nem PIX, informa transferencia ao atendente — saida <strong>Sem solucao</strong>.</li>
                                </ul>
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>6. Saidas do no (conecte no fluxo)</Typography>
                            <Typography variant="body2" component="div" color="text.secondary">
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                    <li><strong>Sem cliente:</strong> CPF/CNPJ nao encontrado ou cliente aguardando atendimento.</li>
                                    <li><strong>OK:</strong> fatura enviada com sucesso.</li>
                                    <li><strong>Sem faturas:</strong> nenhuma fatura no filtro para o contrato/cliente consultado.</li>
                                    <li><strong>Sem solucao:</strong> contrato escolhido com faturas acima do limite de dias, ou fatura sem linha digitavel/PIX — conecte a transferencia para atendente.</li>
                                </ul>
                            </Typography>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setHelpOpen(false)} variant="contained" sx={{ bgcolor: THEME, '&:hover': { bgcolor: THEME_HOVER } }}>
                        Entendi
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default FlowBuilderConsultarFaturasV2Modal;
